import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core'
import {
  ProjectDeliverable,
  ProjectPayload,
  ProjectWithStatus,
} from '@modules/private/worklog/data/interfaces/project.interface'
import { EpicLink } from '@modules/private/worklog/data/interfaces/epic.interface'
import {
  COMMERCIAL_STATUS_META,
  EPIC_CATEGORIES,
  PROJECT_ICONS,
  PROJECT_STATUS_META,
} from '../../data/constants/roadmap.constants'

/**
 * Detalle de un proyecto, y el sitio donde se le bajan los requerimientos.
 *
 * Nace en modo lectura y se edita con un clic, en vez de abrir un formulario aparte: la mayoría de
 * los proyectos entran con nombre y descripción y se enriquecen a lo largo de varias sesiones, así
 * que el trabajo real de esta pantalla es volver a abrir algo y añadirle una línea más.
 *
 * Lo COMERCIAL solo aparece si llegó en la respuesta, y solo llega para Admin: el backend poda el
 * subdocumento entero. Aquí no se decide nada de permisos — se pinta lo que hay.
 */
@Component({
  selector: 'app-roadmap-project-detail',
  templateUrl: './roadmap-project-detail.component.html',
  styleUrls: ['./roadmap-project-detail.component.scss'],
})
export class RoadmapProjectDetailComponent implements OnChanges {
  @Input() row: ProjectWithStatus | null = null
  /** `true` = el bloque comercial es editable. Lo decide el padre con el rol. */
  @Input() canEditPrice = false
  @Input() saving = false
  @Input() error: string | null = null

  @Output() save = new EventEmitter<ProjectPayload>()
  @Output() remove = new EventEmitter<void>()

  editing = false
  draft: ProjectPayload = {}
  deliverables: ProjectDeliverable[] = []
  nuevoEntregable = ''

  readonly PROJECT_STATUS_META = PROJECT_STATUS_META
  readonly COMMERCIAL_STATUS_META = COMMERCIAL_STATUS_META
  readonly CATEGORIES = EPIC_CATEGORIES
  readonly ICONS = PROJECT_ICONS
  readonly COMMERCIAL_STATUSES: { value: string; label: string }[] = Object.entries(COMMERCIAL_STATUS_META).map(
    ([value, m]) => ({ value, label: m.label })
  )

  /**
   * Quita la ficha visual. Va al borrador y no se manda sola: se confirma con Guardar, como el
   * resto de la edición. El `null` es lo que el backend entiende como retirarla — mandar
   * `{ body: '' }` dejaría el subdocumento existiendo y la tarjeta seguiría ofreciéndola.
   */
  dropShowcase(): void {
    this.draft.showcase = null
  }

  ngOnChanges(): void {
    //* Abrir otro proyecto cancela la edición: seguir editando con los datos del anterior a la
    //* vista es la forma más fácil de guardar un cambio en el proyecto equivocado.
    this.editing = false
    this.error = null
  }

  get category() {
    return this.CATEGORIES.find((c) => c.id === this.row?.project.category) ?? null
  }

  get accent(): string {
    return this.category?.color ?? '#6366f1'
  }

  get progress(): number {
    const r = this.row
    return r && r.taskCount ? Math.round((r.doneCount / r.taskCount) * 100) : 0
  }

  priceLabel(): string | null {
    const c = this.row?.project.commercial
    if (!c || c.price == null) return null
    const monto = c.price.toLocaleString('es-MX', { style: 'currency', currency: c.currency || 'MXN', maximumFractionDigits: 0 })
    return `${monto}${c.withVat ? ' c/IVA' : ' + IVA'}`
  }

  /**
   * Los enlaces de un proyecto no son URLs: son rutas dentro de los repos (`api-rendafin/.claude/
   * projects/scope-pipeline.md`). Se pintaban como chips inertes —clic sin efecto— porque no hay
   * nada que abrir desde el navegador. Copiar la ruta sí sirve: es lo que se pega en el editor o
   * en la sesión del agente que va a bajar los requerimientos.
   */
  copied: string | null = null

  copyRef(link: EpicLink): void {
    //* `navigator.clipboard` puede no existir o rechazar por permiso, y sin el respaldo el clic no
    //* hacía NADA: ni copiaba ni avisaba, que es peor que el chip inerte que reemplaza.
    const respaldo = () => {
      const ta = document.createElement('textarea')
      ta.value = link.ref
      ta.style.position = 'fixed'
      ta.style.opacity = '0'
      document.body.appendChild(ta)
      ta.select()
      try {
        document.execCommand('copy')
      } finally {
        document.body.removeChild(ta)
      }
    }
    const avisar = () => {
      this.copied = link.ref
      setTimeout(() => (this.copied = null), 1600)
    }
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(link.ref).then(avisar, () => {
        respaldo()
        avisar()
      })
      return
    }
    respaldo()
    avisar()
  }

  linkIcon(link: EpicLink): string {
    switch (link.type) {
      case 'adr': return 'fa-solid fa-file-contract'
      case 'scope': return 'fa-solid fa-list-check'
      case 'prd': return 'fa-solid fa-file-lines'
      case 'commit': return 'fa-solid fa-code-commit'
      default: return 'fa-solid fa-book'
    }
  }

  // ─────────── Edición ───────────

  startEdit(): void {
    const p = this.row?.project
    if (!p) return
    //* Copia profunda de los entregables: editarlos en sitio mutaría el objeto que la rejilla de
    //* atrás sigue pintando, y cancelar dejaría los cambios puestos igualmente.
    this.deliverables = (p.deliverables ?? []).map((d) => ({ ...d }))
    this.draft = {
      name: p.name,
      description: p.description,
      value: p.value,
      category: p.category,
      icon: p.icon,
      targetDate: p.targetDate,
      ...(this.canEditPrice && p.commercial ? { commercial: { ...p.commercial } } : {}),
    }
    this.nuevoEntregable = ''
    this.editing = true
  }

  cancelEdit(): void {
    this.editing = false
    this.error = null
  }

  addDeliverable(): void {
    const t = this.nuevoEntregable.trim()
    if (!t) return
    this.deliverables.push({ title: t, description: null, done: false })
    this.nuevoEntregable = ''
  }

  dropDeliverable(i: number): void {
    this.deliverables.splice(i, 1)
  }

  /** Alta del bloque comercial en un proyecto que aún no lo tiene. */
  startCommercial(): void {
    this.draft.commercial = { price: null, currency: 'MXN', withVat: false, client: null, status: 'draft', notes: null }
  }

  onSave(): void {
    const payload: ProjectPayload = { ...this.draft, deliverables: this.deliverables }
    //* Sin permiso no se manda el bloque: el backend rechaza el PATCH entero si viene `commercial`
    //* y quien escribe no es Admin, así que enviarlo "por si acaso" rompería un guardado legítimo.
    if (!this.canEditPrice) delete payload.commercial
    this.save.emit(payload)
  }
}
